// @flow
import React from 'react';
import {
  Interval,
  DateTime,
} from 'luxon';

import type {
  Worklog,
  IntervalType,
} from 'chronos-types';

import {
  GridList,
} from '../../styled';

import GridCellRenderer from './GridCellRenderer';
import GridHeaderCellRenderer from './GridHeaderCellRenderer';
import GridFooterCellRenderer from './GridFooterCellRenderer';

import ListRowRenderer from './ListRowRenderer';
import ListHeaderCellRenderer from './ListHeaderCellRenderer';
import ListFooterCellRenderer from './ListFooterCellRenderer';

import {
  TimelineContainer,
} from './styled';


function getItem(sidebarItems, props) {
  const { index } = props;
  if (!sidebarItems[index]) return null;
  return sidebarItems[index];
}

type Worklogs = {
  [string]: {
    [string]: Worklog,
  }
}

type Props = {
  /**
   * row sidebarItems
   * */
  sidebarItems?: Array<Object>,
  /**
   * groupBy one of issues, epics, users, projects, versions.
   * */
  groupBy?: string,
  /**
   * Function that is called after click some groupBy
   * */
  onGroupBy?: { (): void },
  /**
   * groupBy one of issues, epics, users, projects, versions.
   * */
  worklogs: Worklogs,
  /**
   * sidebar width
   * */
  listWidth?: number,
  /**
   * loading state
   * */
  fetching?: boolean,
  /**
   * Luxon
   * */
  currentDate?: IntervalType,
  /**
   * selected rows
   * */
  selectedRows?: Array<number>,
  /**
   * selected columns
   * */
  selectedColumns?: Array<number>,
  /**
   * worklog loading state
   * */
  worklogsFetching?: boolean,
  /**
   * function that is called whenever resize list
   * */
  listResizeRequest?: { (): void },
  /**
   * function that is called after click some row
   * */
  selectRows?: { (): void },
  /**
   * function that is called after click some column
   * */
  onSelect: { (): void },
  setGroupByVisible: (value: boolean) => void,
  isGroupByOpen: boolean,
};

const Timeline = ({
  sidebarItems,
  groupBy,
  worklogs,
  fetching,
  listWidth,
  onGroupBy,
  currentDate,
  selectedRows,
  selectedColumns,
  worklogsFetching,
  listResizeRequest,
  selectRows,
  onSelect,
  setGroupByVisible,
  isGroupByOpen,
}: Props) => {
  const rowCount = sidebarItems.length;
  const rowHeight = 41;
  const dateDiff = currentDate.end.diff(currentDate.start, 'days');

  const listProps = {
    rowCount: fetching ? 20 : rowCount,
    rowHeight,
    headerHeight: rowHeight,
    rowRenderer: props => (
      <ListRowRenderer
        {...props}
        item={getItem(sidebarItems, props)}
        groupBy={groupBy}
        fetching={fetching}
        selectedRows={selectedRows}
        selectRows={selectRows}
      />
    ),
    headerCellRenderer: props => (
      <ListHeaderCellRenderer
        {...props}
        groupBy={groupBy}
        isGroupByOpen={isGroupByOpen}
        onGroupBy={onGroupBy}
        setGroupByVisible={setGroupByVisible}
        listResizeRequest={listResizeRequest}
      />
    ),
    footerCellRenderer: props => (
      <ListFooterCellRenderer
        {...props}
      />
    ),
  };

  const gridProps = {
    rowCount,
    rowHeight,
    headerHeight: rowHeight,
    cellRenderer: props => (
      <GridCellRenderer
        {...props}
        worklogs={worklogs}
        rowCount={rowCount}
        fetching={worklogsFetching}
        currentDate={currentDate}
        sidebarItems={sidebarItems}
        selectedRows={selectedRows}
        selectedColumns={selectedColumns}
      />
    ),
    headerCellRenderer: props => (
      <GridHeaderCellRenderer
        {...props}
        currentDate={currentDate}
        selectedColumns={selectedColumns}
        onSelect={onSelect}
      />
    ),
    footerCellRenderer: props => (
      <GridFooterCellRenderer
        {...props}
        worklogs={worklogs}
        currentDate={currentDate}
      />
    ),
    columnCount: Math.round(dateDiff.get('day')),
    columnWidth: 51,
  };

  return (
    <TimelineContainer>
      <GridList
        listProps={listProps}
        gridProps={gridProps}
        fetching={fetching}
        listWidth={listWidth}
        listResizeRequest={listResizeRequest}
      />
    </TimelineContainer>
  );
};

Timeline.defaultProps = {
  sidebarItems: [],
  groupBy: 'users',
  fetching: false,
  listWidth: 370,
  currentDate: Interval.fromDateTimes(
    DateTime.local().startOf('month'),
    DateTime.local().endOf('month'),
  ),
  selectedRows: [],
  selectedColumns: [],
  worklogsFetching: false,
  onGroupBy: () => {},
  selectRows: () => {},
  selectColumns: () => {},
  unSelectColumns: () => {},
  listResizeRequest: () => {},
};

export default Timeline;
